import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Sparkles,
  Layers,
  CheckCircle2,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Package,
  FlaskConical,
  Droplets,
  Box,
  Tag,
  Clock
} from 'lucide-react';
import CuteEmoji from '@/components/shared/CuteEmoji';

const CATEGORY_ICONS = {
  BOTOL: Package,
  BIBIT: FlaskConical,
  PELARUT: Droplets,
  ALKOHOL: Droplets,
  DPG: Droplets,
  'AQUA DES': Droplets,
  TUTUP: Box,
  SPRAY: Droplets,
  BOX: Box,
  STIKER: Tag,
  KEMASAN: Package,
};

export const formatUnit = (unit) => {
  if (!unit) return '';
  const u = String(unit).trim().toLowerCase();
  if (u === 'l' || u === 'liter' || u === 'ltr') return 'L';
  if (u === 'ml' || u === 'mililiter') return 'ml';
  if (u === 'g' || u === 'gram' || u === 'gr') return 'g';
  if (u === 'kg' || u === 'kilogram') return 'kg';
  if (u === 'pcs' || u === 'pc' || u === 'buah' || u === 'botol' || u === 'biji') return 'pcs';
  return unit;
};

export const formatQty = (val) => {
  if (val === null || val === undefined || isNaN(val)) return '0';
  const num = Number(val);
  return num.toLocaleString('id-ID', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

export default function AsaStockDeductionModal({
  open,
  onOpenChange,
  title = "Konfirmasi Pemotongan Bahan Baku & Kemasan",
  taskName = "",
  poNumber = "",
  brandName = "",
  activityType = "racik", // 'racik' | 'mixing' | 'filling' | 'packing' | 'direct' | 'done'
  previewData = { items: [], hasCatchup: false, catchupStages: [] },
  onConfirm,
  confirmLabel = "Konfirmasi & Mulai Pengerjaan",
  isLoading = false,
}) {
  const [openCategories, setOpenCategories] = useState({});

  const { items = [], hasCatchup = false, catchupStages = [] } = previewData || {};

  // Group items by category
  const groupedByCategory = useMemo(() => {
    const map = {};
    items.forEach(item => {
      const cat = item.category || 'LAINNYA';
      if (!map[cat]) map[cat] = [];
      map[cat].push(item);
    });
    return map;
  }, [items]);

  const categories = Object.keys(groupedByCategory);

  // Toggle category accordion
  const toggleCategory = (cat) => {
    setOpenCategories(prev => ({
      ...prev,
      [cat]: prev[cat] === undefined ? false : !prev[cat]
    }));
  };

  // Dynamic ASA Greeting Message
  const getAsaMessage = () => {
    const act = (activityType || '').toLowerCase();
    if (act === 'direct' || act === 'done' || act === 'shipped') {
      return `Halo! Pesanan ini akan diselesaikan. Seluruh sisa bahan baku & kemasan di bawah ini akan dipotong dari stok dan otomatis dicatat ke riwayat Barang Keluar ya! ✨`;
    }
    if (act === 'racik' || act === 'mixing') {
      return `Halo! Saat proses Mixing dimulai, bahan formula (bibit & pelarut) berikut akan dipotong dari stok gudang. Mohon diperiksa rinciannya ya! 🧪`;
    }
    if (act === 'filling') {
      return `Halo! Saat proses Filling dimulai, botol, tutup, dan spray berikut akan dipotong dari stok gudang. Pastikan fisik kemasan sudah siap! 🧴`;
    }
    if (['packing', 'packaging', 'labeling', 'sealing'].includes(act)) {
      return `Halo! Saat proses Pengemasan dimulai, dus, stiker, dan kemasan sekunder berikut akan dialokasikan & dipotong dari stok. 📦`;
    }
    return `Halo! Berikut adalah daftar pemotongan bahan baku & kemasan untuk tahapan tugas ini.`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 overflow-hidden rounded-[2rem] border border-amber-200/60 shadow-2xl bg-white">
        
        {/* Header with ASA Assistant */}
        <div className="bg-gradient-to-r from-amber-50 via-[#FFFDF7] to-amber-100/50 p-6 border-b border-amber-200/60 shrink-0">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white shadow-md shrink-0 ring-4 ring-amber-100">
              <Sparkles className="w-6 h-6 text-white animate-pulse" />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <Badge variant="outline" className="text-[10px] font-black uppercase tracking-wider bg-amber-500/10 text-amber-900 border-amber-300">
                  ASA AI ASSISTANT
                </Badge>
                {brandName && (
                  <span className="text-xs font-bold text-slate-700 truncate">
                    {brandName} {poNumber ? `• ${poNumber}` : ''}
                  </span>
                )}
              </div>

              <h2 className="text-lg font-black text-slate-900 tracking-tight">
                {title}
              </h2>

              <p className="text-xs text-slate-600 font-medium leading-relaxed mt-1.5 bg-white/80 p-2.5 rounded-xl border border-amber-200/50 shadow-2xs">
                {getAsaMessage()}
              </p>
            </div>
          </div>

          {/* Catch-up Alert (Curi Start) */}
          {hasCatchup && (
            <div className="mt-3 p-3 bg-indigo-50/90 border border-indigo-200 rounded-xl flex items-start gap-2.5 shadow-2xs animate-in fade-in slide-in-from-top-2">
              <AlertTriangle className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />
              <div className="text-xs text-indigo-900 font-medium leading-tight">
                <strong className="font-bold">Auto-Catchup Terdeteksi:</strong> Tahap sebelumnya ({catchupStages.join(', ')}) belum tercatat dipotong terpisah, sehingga bahan dari tahap tersebut ikut diperhitungkan otomatis di sini agar stok tetap presisi & tidak bocor.
              </div>
            </div>
          )}

          {/* Shortage / Zero Stock Warning Banner */}
          {previewData?.hasShortage && (
            <div className="mt-3 p-3.5 bg-rose-50 border border-rose-200 rounded-xl flex items-start gap-3 shadow-2xs animate-in fade-in slide-in-from-top-2">
              <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
              <div className="text-xs text-rose-900 leading-relaxed flex-1">
                <strong className="font-black text-rose-700 block mb-0.5">⚠️ Perhatian: Ada Item yang Stoknya Kosong (0) / Kurang!</strong>
                Terdapat {previewData.shortageItems?.length || 0} bahan/kemasan yang tercatat kurang dari kebutuhan tugas ini.
                <div className="mt-1.5 font-medium text-rose-800 bg-rose-100/70 p-2.5 rounded-lg border border-rose-200">
                  📢 <strong>Segera infokan ke tim terkait (Purchasing / Gudang)</strong> atau update master stok di menu <strong>Inventori</strong> agar data stok fisik dan sistem terintegrasi dengan rapi.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Scrollable Body: Item list */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4 max-h-[50vh]">
          {items.length === 0 ? (
            <div className="text-center py-10 text-slate-400 bg-slate-50/60 rounded-2xl border border-dashed border-slate-200">
              <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-500 opacity-60" />
              <p className="text-sm font-bold text-slate-700">Semua Stok Sudah Pernah Dipotong</p>
              <p className="text-xs text-slate-400 mt-1">Tidak ada bahan baku atau kemasan baru yang perlu dipotong untuk tahapan ini.</p>
            </div>
          ) : (
            categories.map(cat => {
              const catItems = groupedByCategory[cat] || [];
              const isOpen = openCategories[cat] !== false; // default open
              const IconComp = CATEGORY_ICONS[cat] || Package;

              return (
                <div key={cat} className="border border-slate-200 rounded-2xl overflow-hidden shadow-2xs bg-white transition-all">
                  {/* Category Dropdown Header */}
                  <button
                    type="button"
                    onClick={() => toggleCategory(cat)}
                    className="w-full px-4 py-3 bg-slate-50/80 hover:bg-slate-100 flex items-center justify-between transition-colors border-b border-slate-200/60"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-lg bg-emerald-100 text-emerald-800 flex items-center justify-center">
                        <IconComp className="w-3.5 h-3.5" />
                      </div>
                      <span className="text-xs font-black text-slate-800 uppercase tracking-wide">
                        {cat}
                      </span>
                      <Badge variant="outline" className="text-[10px] font-bold bg-white text-slate-600 border-slate-300">
                        {catItems.length} Item
                      </Badge>
                    </div>

                    <div className="flex items-center gap-2 text-slate-400">
                      <span className="text-[10px] font-semibold">{isOpen ? 'Tutup' : 'Buka'}</span>
                      {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </button>

                  {/* Item Rows */}
                  {isOpen && (
                    <div className="divide-y divide-slate-100 p-2 space-y-1">
                      {catItems.map((item, idx) => {
                        const isItemShortage = item.isShortage || item.isZero;
                        return (
                          <div
                            key={item.id || idx}
                            className={`p-3 rounded-xl transition-colors flex flex-col sm:flex-row sm:items-center justify-between gap-3 border ${
                              isItemShortage 
                                ? 'bg-rose-50/50 border-rose-200 hover:bg-rose-50' 
                                : 'hover:bg-slate-50/80 border-transparent hover:border-slate-200'
                            }`}
                          >
                            {/* Item Identity */}
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5 flex-wrap mb-1">
                                <span className="text-xs font-black text-slate-900 tracking-tight">
                                  {item.name}
                                </span>
                                {item.isCatchup && (
                                  <Badge className="text-[9px] px-1.5 py-0 font-bold bg-indigo-100 text-indigo-700 border-indigo-200">
                                    Tahap {item.stage}
                                  </Badge>
                                )}
                                {item.isZero ? (
                                  <Badge className="text-[9px] px-1.5 py-0 font-black bg-rose-600 text-white border-0">
                                    Stok Kosong (0)
                                  </Badge>
                                ) : item.isShortage ? (
                                  <Badge className="text-[9px] px-1.5 py-0 font-black bg-amber-500 text-white border-0">
                                    Stok Kurang
                                  </Badge>
                                ) : null}
                              </div>
                              
                              <div className="text-[11px] text-slate-500 flex items-center gap-2">
                                <span>Stok Sekarang: <strong className={`font-bold ${isItemShortage ? 'text-rose-600' : 'text-slate-800'}`}>{formatQty(item.currentStock)} {formatUnit(item.unit)}</strong></span>
                              </div>
                            </div>

                            {/* Deduction & Remaining Stock Badges */}
                            <div className="flex items-center gap-2 shrink-0">
                              {/* Red Terpakai Badge */}
                              <div className="px-3 py-1.5 rounded-xl bg-rose-50 border border-rose-200 flex flex-col items-end">
                                <span className="text-xs font-black text-rose-600">
                                  -{formatQty(item.deductQty)} {formatUnit(item.unit)}
                                </span>
                                <span className="text-[9px] font-bold text-rose-500/80 uppercase tracking-tighter">
                                  Kebutuhan
                                </span>
                              </div>

                              {/* Remaining Stock Badge */}
                              <div className={`px-3 py-1.5 rounded-xl border flex flex-col items-end min-w-[70px] ${
                                item.remainingStock <= 0 
                                  ? 'bg-slate-100 border-slate-200' 
                                  : 'bg-emerald-50 border-emerald-200'
                              }`}>
                                <span className={`text-xs font-black ${item.remainingStock <= 0 ? 'text-slate-500' : 'text-emerald-700'}`}>
                                  {formatQty(item.remainingStock)} {formatUnit(item.unit)}
                                </span>
                                <span className={`text-[9px] font-bold uppercase tracking-tighter ${item.remainingStock <= 0 ? 'text-slate-400' : 'text-emerald-600/80'}`}>
                                  Sisa Stok
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer Actions */}
        <DialogFooter className="bg-slate-50/80 p-4 border-t border-slate-200 flex flex-row items-center justify-end gap-2 shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
            className="h-10 px-5 rounded-xl font-bold text-slate-700 hover:bg-slate-100 border-slate-300"
          >
            Batal / Tidak Jadi
          </Button>

          <Button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className={`h-10 px-6 rounded-xl font-black text-white shadow-md gap-2 ${
              previewData?.hasShortage
                ? 'bg-gradient-to-r from-amber-600 to-amber-800 hover:from-amber-700 hover:to-amber-900'
                : 'bg-gradient-to-r from-amber-500 to-[#8C6208] hover:from-amber-600 hover:to-[#735006]'
            }`}
          >
            {isLoading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <CheckCircle2 className="w-4 h-4" />
            )}
            {previewData?.hasShortage ? 'Tetap Mulai Pengerjaan' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
